import { Popover } from "@blueprintjs/core";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import moment from "moment";
import React, { useState } from "react";
import { PlanApproval, PlanApprovalPush, shiftCaption } from "../models/plan-approvals";
import styles from "./ApprovalsPanel.module.scss";

interface ApprovalsPanelProps {
    approvals: PlanApproval[];
    busy: boolean;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    /** Показать на графике, куда предложено сдвинуть объект */
    onReveal: (approval: PlanApproval, push: PlanApprovalPush) => void;
    /** Показать причину — предшественника, который уже стоит на новом месте */
    onCause: (push: PlanApprovalPush) => void;
    /** Согласовать (comment пуст) или отклонить с причиной */
    onDecide: (approval: PlanApproval, approve: boolean, comment?: string) => void;
}

/**
 * «Согласования: N» в шапке графика (этап 6.5): запросы на сдвиг по связям, которые касаются
 * пользователя. ГИП согласует или отклоняет с причиной; автор видит, что его запрос ждёт.
 * Предложенное место каждого объекта — фиолетовый контур на графике, «Показать» подводит к нему,
 * «Причина» — к предшественнику, из-за которого сдвиг.
 */
export const ApprovalsPanel: React.FC<ApprovalsPanelProps> = (props) => {
    const { approvals, busy, isOpen, onOpenChange, onReveal, onCause, onDecide } = props;

    /** Запрос, для которого вводят причину отказа */
    const [rejecting, setRejecting] = useState<string | null>(null);
    const [comment, setComment] = useState("");

    if (!approvals.length) {
        return null;
    }

    const toDecide = approvals.filter((approval) => approval.canDecide).length;

    return (
        <Popover
            isOpen={isOpen}
            onInteraction={(next) => onOpenChange(next)}
            placement="bottom"
            minimal={true}
            content={
                <div className={styles.list}>
                    {approvals.map((approval) => (
                        <div key={approval.id} className={styles.item}>
                            <div className={styles.head}>
                                <b>{approval.orderLabel}</b>
                                <span className={styles.meta}>
                                    {approval.authorName} · {moment(approval.created).format("DD.MM HH:mm")}
                                </span>
                            </div>

                            {approval.pushes.map((push) => (
                                <div
                                    key={`${push.kind}:${push.assignmentId || push.contentGuid}`}
                                    className={styles.push}
                                >
                                    <span className={styles.label}>{push.label || "Работа"}</span>
                                    <span className={styles.shift}>{shiftCaption(push.shift)}</span>
                                    <button
                                        type="button"
                                        className={styles.link}
                                        onClick={() => onReveal(approval, push)}
                                        title="Показать на графике, куда сдвинется"
                                    >
                                        Показать
                                    </button>
                                    {push.cause && (
                                        <button
                                            type="button"
                                            className={styles.link}
                                            onClick={() => onCause(push)}
                                            title={`Связь ${push.linkCaption}: предшественник уже сдвинут`}
                                        >
                                            Причина: {push.cause.label}
                                        </button>
                                    )}
                                </div>
                            ))}

                            {approval.canDecide ? (
                                rejecting === approval.id ? (
                                    <div className={styles.reject}>
                                        <input
                                            className="planr-default-input"
                                            autoFocus={true}
                                            placeholder="Причина отказа — её увидит автор"
                                            value={comment}
                                            onChange={(event) => setComment(event.target.value)}
                                        />
                                        <PlanrButton
                                            type="danger"
                                            size="small"
                                            disabled={busy}
                                            onClick={() => {
                                                onDecide(approval, false, comment);
                                                setRejecting(null);
                                                setComment("");
                                            }}
                                        >
                                            Отклонить
                                        </PlanrButton>
                                        <PlanrButton type="secondary" size="small" onClick={() => setRejecting(null)}>
                                            Назад
                                        </PlanrButton>
                                    </div>
                                ) : (
                                    <div className={styles.actions}>
                                        <PlanrButton
                                            type="greenish"
                                            size="small"
                                            disabled={busy}
                                            onClick={() => onDecide(approval, true)}
                                        >
                                            Согласовать
                                        </PlanrButton>
                                        <PlanrButton
                                            type="lightdanger"
                                            size="small"
                                            disabled={busy}
                                            onClick={() => {
                                                setRejecting(approval.id);
                                                setComment("");
                                            }}
                                        >
                                            Отклонить
                                        </PlanrButton>
                                    </div>
                                )
                            ) : (
                                <div className={styles.waiting}>
                                    Ждёт решения ГИПа{approval.gipName ? ` ${approval.gipName}` : ""}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            }
        >
            <span
                className={toDecide ? styles.chip : styles.chipWaiting}
                data-tip={
                    toDecide
                        ? "Сдвиги по связям в ваших проектах ждут вашего решения"
                        : "Ваши сдвиги по связям ждут решения ГИПов"
                }
            >
                Согласования: <b>{toDecide || approvals.length}</b>
            </span>
        </Popover>
    );
};
